import { startOfMinute, subDays, addSeconds, subSeconds } from "date-fns";
import { RETENTION_PERIOD_IN_DAYS } from "constants/time.constants";
import { PlateSearchReportDetails } from "types/reports/interfaces";
import { WINDOW_DURATION_MS, SEGMENT_DURATION_MS, MAX_CENTER_OFFSET_RATIO } from "constants/report.constants";
import { VideoRequestData } from "types/incident/video-request";

/**
 * Computes a ~59s video request window centered on the sighting time T,
 * preferring alignment to integer-minute recording segments.
 *
 * Priority order:
 * 1. Hard constraint: window must lie within [earliestAvailable, latestAvailable]
 * 2. Soft preference: align to minute segment to avoid cross-segment stitching
 * 3. Soft preference: keep T near center (±15% offset)
 */
const computeVideoRequestWindow = (inTime: Date | string): { startTime: Date; endTime: Date } => {
    const T = new Date(inTime);
    const maxAllowedOffset = MAX_CENTER_OFFSET_RATIO * WINDOW_DURATION_MS;

    // Step 1: T-centered default window [T-29s, T+30s]
    const defaultStart = subSeconds(T, 29).getTime();
    const defaultEnd = addSeconds(T, 30).getTime();

    // Step 2: Try aligning to the minute segment T sits in
    // Clamp range is [mFloor, mFloor + 1s] since 59s window in 60s segment
    const mFloor = startOfMinute(T).getTime();
    const clampUpper = mFloor + SEGMENT_DURATION_MS - WINDOW_DURATION_MS;
    const shiftedStart = Math.max(mFloor, Math.min(defaultStart, clampUpper));
    const center = shiftedStart + WINDOW_DURATION_MS / 2;
    const offset = Math.abs(center - T.getTime());

    let finalStart: Date;
    let finalEnd: Date;

    if (offset <= maxAllowedOffset) {
        finalStart = new Date(shiftedStart);
        finalEnd = addSeconds(shiftedStart, WINDOW_DURATION_MS / 1000);
    } else {
        // Fall back to unaligned T-centered window
        finalStart = new Date(defaultStart);
        finalEnd = new Date(defaultEnd);
    }

    // Step 3: Apply retention clamping (hard constraint)
    const now = new Date();
    const earliestAvailable = subDays(now, RETENTION_PERIOD_IN_DAYS);
    if (finalStart < earliestAvailable) {
        finalStart = earliestAvailable;
        finalEnd = addSeconds(finalStart, WINDOW_DURATION_MS / 1000);
        if (finalEnd > now) finalEnd = now;
    }
    if (finalEnd > now) {
        finalEnd = now;
        finalStart = subSeconds(finalEnd, WINDOW_DURATION_MS / 1000);
        if (finalStart < earliestAvailable) finalStart = earliestAvailable;
    }

    return { startTime: finalStart, endTime: finalEnd };
};

const generateVideoRequestParams = (row: PlateSearchReportDetails): VideoRequestData => {
    const { locationId, metaField1, inTime, metaData } = row;
    const { deviceId, cameraId } = metaData;

    // Compute timing window
    const { startTime, endTime } = computeVideoRequestWindow(inTime);

    // Build reason text
    const reason = `Plate sighting: ${metaField1}`;

    return {
        locationId,
        deviceId,
        cameraId,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        reason,
    };
};

export default generateVideoRequestParams;
